import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  Search, 
  Filter,
  Star,
  MapPin,
  Building2,
  CheckCircle2,
  Award,
  Calendar,
  Mail,
  Phone,
  ExternalLink,
  Users,
  FileCheck,
  Globe,
  Clock,
  ChevronRight,
  Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auditor Card Component
const AuditorCard = ({ auditor, onSelect, onRequestQuote }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all">
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
            {auditor.name?.split(' ').map(w => w[0]).join('').slice(0, 2)}
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">{auditor.name}</h3>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <MapPin size={12} />
                  {auditor.headquarters}
                </p>
              </div>
              <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-full">
                <Star className="text-amber-500" size={14} fill="currentColor" />
                <span className="text-sm font-medium text-amber-700">{auditor.rating}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {auditor.certifications?.slice(0, 3).map((cert, idx) => (
            <span key={idx} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-full flex items-center gap-1">
              <CheckCircle2 size={10} />
              {cert}
            </span>
          ))}
          {auditor.certifications?.length > 3 && (
            <span className="text-xs text-slate-500">+{auditor.certifications.length - 3} more</span>
          )}
        </div>

        <p className="mt-3 text-sm text-slate-600 line-clamp-2">{auditor.description}</p>

        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <div className="bg-slate-50 rounded-lg p-2">
            <p className="text-lg font-bold text-slate-900">{auditor.clients_served}+</p>
            <p className="text-xs text-slate-500">Clients</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-2">
            <p className="text-lg font-bold text-slate-900">{auditor.years_experience}</p>
            <p className="text-xs text-slate-500">Years Exp.</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-2">
            <p className="text-lg font-bold text-slate-900">{auditor.auditors_count}</p>
            <p className="text-xs text-slate-500">Auditors</p>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            onClick={() => onSelect(auditor)}
            className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            View Profile
          </button>
          <button
            onClick={() => onRequestQuote(auditor)}
            className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
          >
            Request Quote
          </button>
        </div>
      </div>
    </div>
  );
};

// Auditor Detail Modal
const AuditorDetailModal = ({ auditor, onClose, onRequestQuote }) => {
  if (!auditor) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-2xl">
              {auditor.name?.split(' ').map(w => w[0]).join('').slice(0, 2)}
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-900">{auditor.name}</h2>
              <p className="text-slate-500 flex items-center gap-1">
                <MapPin size={14} />
                {auditor.headquarters}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-full">
                  <Star className="text-amber-500" size={14} fill="currentColor" />
                  <span className="text-sm font-medium text-amber-700">{auditor.rating}</span>
                </div>
                <span className="text-sm text-slate-500">{auditor.reviews_count} reviews</span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="font-semibold text-slate-900 mb-2">About</h3>
            <p className="text-slate-600">{auditor.description}</p>
          </div>

          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Certifications & Accreditations</h3>
            <div className="flex flex-wrap gap-2">
              {auditor.certifications?.map((cert, idx) => (
                <span key={idx} className="bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-full text-sm flex items-center gap-1">
                  <Award size={14} />
                  {cert}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Services Offered</h3>
            <div className="grid grid-cols-2 gap-3">
              {auditor.services?.map((service, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle2 size={14} className="text-emerald-500" />
                  {service}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Industry Expertise</h3>
            <div className="flex flex-wrap gap-2">
              {auditor.industries?.map((industry, idx) => (
                <span key={idx} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">
                  {industry}
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <h4 className="text-sm font-medium text-slate-500 mb-2">Contact</h4>
              <div className="space-y-2">
                <p className="text-sm text-slate-700 flex items-center gap-2">
                  <Mail size={14} className="text-slate-400" />
                  {auditor.email}
                </p>
                <p className="text-sm text-slate-700 flex items-center gap-2">
                  <Phone size={14} className="text-slate-400" />
                  {auditor.phone}
                </p>
                <p className="text-sm text-slate-700 flex items-center gap-2">
                  <Globe size={14} className="text-slate-400" />
                  {auditor.website}
                </p>
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <h4 className="text-sm font-medium text-slate-500 mb-2">Availability</h4>
              <div className="space-y-2">
                <p className="text-sm text-slate-700 flex items-center gap-2">
                  <Clock size={14} className="text-slate-400" />
                  Lead time: {auditor.lead_time || '2-4 weeks'}
                </p>
                <p className="text-sm text-slate-700 flex items-center gap-2">
                  <Calendar size={14} className="text-slate-400" />
                  Next available: {auditor.next_available || 'March 2026'}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => onRequestQuote(auditor)}
            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700"
          >
            Request a Quote
          </button>
        </div>
      </div>
    </div>
  );
};

// Request Quote Modal
const RequestQuoteModal = ({ auditor, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    scope: '',
    timeline: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setSubmitted(true);
  };

  if (!auditor) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900">Request Quote</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">×</button>
          </div>
          <p className="text-sm text-slate-500 mt-1">From {auditor.name}</p>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="text-emerald-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Quote Requested!</h3>
              <p className="text-slate-600 mb-4">
                {auditor.name} will contact you within 2 business days with a detailed proposal.
              </p>
              <button onClick={onClose} className="px-6 py-2 bg-indigo-600 text-white rounded-lg">
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Company Name</label>
                <input
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Name</label>
                  <input
                    type="text"
                    required
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Assurance Scope</label>
                <select
                  value={formData.scope}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="">Select scope...</option>
                  <option value="limited_esrs">Limited Assurance - ESRS</option>
                  <option value="reasonable_esrs">Reasonable Assurance - ESRS</option>
                  <option value="limited_climate">Limited Assurance - Climate Only</option>
                  <option value="full_csrd">Full CSRD Compliance Audit</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Preferred Timeline</label>
                <select
                  value={formData.timeline}
                  onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="">Select timeline...</option>
                  <option value="q1_2026">Q1 2026</option>
                  <option value="q2_2026">Q2 2026</option>
                  <option value="q3_2026">Q3 2026</option>
                  <option value="q4_2026">Q4 2026</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Additional Information</label>
                <textarea
                  rows={3}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg"
                  placeholder="Any specific requirements or questions..."
                />
              </div>
              <button
                type="submit"
                className="w-full py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700"
              >
                Submit Request
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
const AuditorCatalogue = () => {
  const [loading, setLoading] = useState(true);
  const [auditors, setAuditors] = useState([]);
  const [selectedAuditor, setSelectedAuditor] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteAuditor, setQuoteAuditor] = useState(null);
  const [filters, setFilters] = useState({ certification: '', industry: '', region: '' });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadAuditors();
  }, [filters]);

  const loadAuditors = async () => {
    setLoading(true);
    try {
      // API call would go here
      setAuditors(generateMockAuditors());
    } catch (err) {
      console.error('Failed to load auditors:', err);
      setAuditors(generateMockAuditors());
    } finally {
      setLoading(false);
    }
  };

  const generateMockAuditors = () => [
    {
      id: '1',
      name: 'EY Climate & Sustainability',
      headquarters: 'London, UK',
      rating: 4.9,
      reviews_count: 156,
      clients_served: 500,
      years_experience: 15,
      auditors_count: 120,
      description: 'Global leader in sustainability assurance with deep expertise in CSRD, ESRS, and climate-related disclosures. Trusted by Fortune 500 companies worldwide.',
      certifications: ['ISAE 3000', 'ISAE 3410', 'AA1000AS', 'ISO 14064'],
      services: ['CSRD Assurance', 'ESRS Verification', 'Climate Risk Assessment', 'Supply Chain Audits', 'GHG Verification', 'SDG Reporting'],
      industries: ['Technology', 'Manufacturing', 'Financial Services', 'Energy', 'Retail'],
      email: 'sustainability@ey.com',
      phone: '+44 20 7951 2000',
      website: 'ey.com/sustainability',
      lead_time: '3-4 weeks',
      next_available: 'March 2026'
    },
    {
      id: '2',
      name: 'Deloitte ESG Assurance',
      headquarters: 'New York, USA',
      rating: 4.8,
      reviews_count: 142,
      clients_served: 450,
      years_experience: 12,
      auditors_count: 95,
      description: 'Comprehensive ESG assurance services with a focus on regulatory compliance and stakeholder confidence. Pioneers in integrated reporting assurance.',
      certifications: ['ISAE 3000', 'SOC 2', 'AA1000AS', 'GRI Certified'],
      services: ['CSRD Readiness', 'SEC Climate Disclosure', 'Third-party Verification', 'Materiality Assessment', 'Stakeholder Engagement Audit'],
      industries: ['Healthcare', 'Financial Services', 'Consumer Products', 'Technology'],
      email: 'esg@deloitte.com',
      phone: '+1 212 492 4000',
      website: 'deloitte.com/esg',
      lead_time: '2-3 weeks',
      next_available: 'February 2026'
    },
    {
      id: '3',
      name: 'PwC Sustainability Practice',
      headquarters: 'Amsterdam, Netherlands',
      rating: 4.7,
      reviews_count: 128,
      clients_served: 380,
      years_experience: 14,
      auditors_count: 85,
      description: 'European sustainability assurance leader with unmatched CSRD expertise. Helping organizations navigate the evolving regulatory landscape.',
      certifications: ['ISAE 3000', 'ISAE 3410', 'DNV GL', 'EU Taxonomy Verified'],
      services: ['EU Taxonomy Alignment', 'SFDR Compliance', 'Double Materiality', 'Scope 3 Verification', 'TCFD Reporting'],
      industries: ['Banking', 'Insurance', 'Real Estate', 'Automotive', 'Chemicals'],
      email: 'sustainability@pwc.com',
      phone: '+31 88 792 0000',
      website: 'pwc.com/sustainability',
      lead_time: '4-5 weeks',
      next_available: 'April 2026'
    },
    {
      id: '4',
      name: 'Bureau Veritas Certification',
      headquarters: 'Paris, France',
      rating: 4.6,
      reviews_count: 98,
      clients_served: 600,
      years_experience: 20,
      auditors_count: 200,
      description: 'World leader in testing, inspection, and certification with extensive ESG verification capabilities across all industries and regions.',
      certifications: ['ISO 14001', 'ISO 14064', 'SA8000', 'LEED', 'BREEAM'],
      services: ['Carbon Footprint Verification', 'Supply Chain Audits', 'Environmental Management', 'Social Compliance', 'Product LCA'],
      industries: ['Manufacturing', 'Construction', 'Logistics', 'Agriculture', 'Mining'],
      email: 'esg@bureauveritas.com',
      phone: '+33 1 55 24 70 00',
      website: 'bureauveritas.com',
      lead_time: '2-3 weeks',
      next_available: 'February 2026'
    },
  ];

  const filteredAuditors = auditors.filter(a =>
    (!searchQuery || a.name.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!filters.certification || a.certifications.includes(filters.certification)) &&
    (!filters.industry || a.industries.includes(filters.industry))
  );

  const handleRequestQuote = (auditor) => {
    setQuoteAuditor(auditor);
    setShowQuoteModal(true);
    setShowDetailModal(false);
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="auditor-catalogue">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Shield className="text-indigo-600" size={28} />
          Find Auditor
        </h1>
        <p className="text-slate-600 mt-1">Find and engage verified ESG assurance providers</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <Building2 className="text-indigo-500 mb-2" size={20} />
          <p className="text-2xl font-bold text-slate-900">{auditors.length}</p>
          <p className="text-sm text-slate-500">Verified Auditors</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <Award className="text-amber-500 mb-2" size={20} />
          <p className="text-2xl font-bold text-slate-900">15+</p>
          <p className="text-sm text-slate-500">Certifications</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <Globe className="text-emerald-500 mb-2" size={20} />
          <p className="text-2xl font-bold text-slate-900">40+</p>
          <p className="text-sm text-slate-500">Countries Covered</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <Users className="text-blue-500 mb-2" size={20} />
          <p className="text-2xl font-bold text-slate-900">500+</p>
          <p className="text-sm text-slate-500">Expert Auditors</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search auditors..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg"
          />
        </div>
        <select
          value={filters.certification}
          onChange={(e) => setFilters({ ...filters, certification: e.target.value })}
          className="px-4 py-2 border border-slate-200 rounded-lg"
        >
          <option value="">All Certifications</option>
          <option value="ISAE 3000">ISAE 3000</option>
          <option value="AA1000AS">AA1000AS</option>
          <option value="ISO 14064">ISO 14064</option>
        </select>
        <select
          value={filters.industry}
          onChange={(e) => setFilters({ ...filters, industry: e.target.value })}
          className="px-4 py-2 border border-slate-200 rounded-lg"
        >
          <option value="">All Industries</option>
          <option value="Technology">Technology</option>
          <option value="Manufacturing">Manufacturing</option>
          <option value="Financial Services">Financial Services</option>
          <option value="Energy">Energy</option>
        </select>
      </div>

      {/* Auditors Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {filteredAuditors.map((auditor, idx) => (
            <AuditorCard
              key={idx}
              auditor={auditor}
              onSelect={(a) => {
                setSelectedAuditor(a);
                setShowDetailModal(true);
              }}
              onRequestQuote={handleRequestQuote}
            />
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && (
        <AuditorDetailModal
          auditor={selectedAuditor}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAuditor(null);
          }}
          onRequestQuote={handleRequestQuote}
        />
      )}

      {/* Quote Modal */}
      {showQuoteModal && (
        <RequestQuoteModal
          auditor={quoteAuditor}
          onClose={() => {
            setShowQuoteModal(false);
            setQuoteAuditor(null);
          }}
          onSubmit={(data) => console.log('Quote requested:', data)}
        />
      )}
    </div>
  );
};

export default AuditorCatalogue;
